<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-cash-outline" :title="$t('no_data')" :text="$t('select_date_and_filters')"></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl rd-tbl--workers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-id" :aria-sort="ariaSort('userId')">
              <button type="button" @click="sortBy('userId')">ID {{ sortArrow("userId") }}</button>
            </th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("name") }} {{ sortArrow("name") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordersCount')">
              <button type="button" @click="sortBy('ordersCount')">{{ $t("orders") }} {{ sortArrow("ordersCount") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordersTotal')">
              <button type="button" @click="sortBy('ordersTotal')">
                {{ $t("orders_total") }}{{ currencySuffix }} {{ sortArrow("ordersTotal") }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('spent')">
              <button type="button" @click="sortBy('spent')">
                {{ $t("spent_on_credits") }}{{ currencySuffix }} {{ sortArrow("spent") }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('balance')">
              <button type="button" @click="sortBy('balance')">
                {{ $t("credit_balance") }}{{ currencySuffix }} {{ sortArrow("balance") }}
              </button>
            </th>
            <th scope="col" class="cu-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.key"
            data-open
            tabindex="0"
            @click="openUserDialog(row.source)"
            @keydown.enter.prevent="openUserDialog(row.source)"
          >
            <td class="rd-col-id rd-num rd-muted">{{ row.userId }}</td>
            <td class="is-name" :title="row.name">
              <span class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.name }}</span>
              </span>
            </td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.ordersCount }">{{ row.ordersCount }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.ordersTotal }">{{ money(row.ordersTotal) }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.spent }">{{ money(row.spent) }}</td>
            <td class="is-right rd-num" :class="{ 'is-alert': row.balance < 0 }">{{ money(row.balance) }}</td>
            <td class="cu-col-actions">
              <span class="cu-actions">
                <button
                  type="button"
                  class="rd-ib rd-ib--sm"
                  :title="$t('tooltip_order_details')"
                  :aria-label="$t('tooltip_order_details')"
                  @click.stop="openUserDialog(row.source)"
                >
                  <v-icon size="18">mdi-format-list-bulleted</v-icon>
                </button>
                <button
                  type="button"
                  class="rd-ib rd-ib--sm"
                  :title="$t('topup_history')"
                  :aria-label="$t('topup_history')"
                  @click.stop="openHistoryDialog(row.source)"
                >
                  <v-icon size="18">mdi-history</v-icon>
                </button>
                <button
                  type="button"
                  class="rd-ib rd-ib--sm"
                  :title="$t('add_credit')"
                  :aria-label="$t('add_credit')"
                  @click.stop="addCredit(row.source)"
                >
                  <v-icon size="18">mdi-cash-plus</v-icon>
                </button>
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="rd-col-id"></td>
            <td>{{ $t("total") }}</td>
            <td class="is-right rd-num">{{ sums.ordersCount }}</td>
            <td class="is-right rd-num">{{ money(sums.ordersTotal) }}</td>
            <td class="is-right rd-num">{{ money(sums.spent) }}</td>
            <td class="is-right rd-num">{{ money(sums.balance) }}</td>
            <td class="cu-col-actions"></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <credit-user-details-dialog-limit-transactions
      v-model="showUserDialog"
      :user="selectedUser"
    ></credit-user-details-dialog-limit-transactions>
    <credit-user-history-dialog
      v-model="showHistoryDialog"
      :user="selectedHistoryUser"
      @navigate-to-topup="handleNavigateToTopup"
    ></credit-user-history-dialog>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabCreditUsers from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabCreditUsers.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

const initialsOf = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("");

/**
 * Kreditni korisnici firme "više restorana" na računaru: po korisniku broj narudžbi, ukupan iznos, potrošeni kredit i
 * stanje (crveno kad je u minusu). Klik na red otvara narudžbe korisnika, a dugmad istorijat dopuna i dodavanje
 * kredita (otvara "Dodaj kredit" sa tim korisnikom). Restorane i period čita iz zajedničke trake. Nasleđuje
 * učitavanje, dijaloge, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiCreditUsers",
  extends: CompanyMultiRestaurantTabCreditUsers,
  mixins: [hasMultiFeed, hasTableSort],
  components: { ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    currencySuffix() {
      return this.majorityRestaurantCurrency ? ` (${this.majorityRestaurantCurrency})` : "";
    },
    rows() {
      return this.sortRows(
        this.users.map((user, index) => {
          const name = this.toLatin(user.user_name || "");
          return {
            key: `${user.user_id}-${index}`,
            userId: user.user_id,
            name,
            initials: initialsOf(name),
            ordersCount: user.orders_count || 0,
            ordersTotal: Number(this.formatMoneyField(user, "orders_total")) || 0,
            spent: Number(this.formatMoneyField(user, "spent_on_credits")) || 0,
            balance: Number(this.formatMoneyField(user, "current_credit_balance")) || 0,
            source: user,
          };
        })
      );
    },
    sums() {
      return this.rows.reduce(
        (sums, row) => ({
          ordersCount: sums.ordersCount + row.ordersCount,
          ordersTotal: sums.ordersTotal + row.ordersTotal,
          spent: sums.spent + row.spent,
          balance: sums.balance + row.balance,
        }),
        { ordersCount: 0, ordersTotal: 0, spent: 0, balance: 0 }
      );
    },
    kpis() {
      return [
        { key: "users", label: this.$t("users"), value: this.rows.length, tone: "inverse" },
        { key: "spent", label: this.$t("spent_on_credits"), value: this.money(this.sums.spent) },
        { key: "balance", label: this.$t("credit_balance"), value: this.money(this.sums.balance) },
      ];
    },
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    /** "Dodaj kredit" za korisnika: ljuska otvara taj izveštaj sa izabranim korisnikom. */
    addCredit(user) {
      this.$emit("open-screen", "addCredit", { userId: user.user_id });
    },
    handleNavigateToTopup(userId) {
      this.$emit("open-screen", "addCredit", { userId });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("name"),
        this.$t("orders"),
        this.$t("orders_total"),
        this.$t("spent_on_credits"),
        this.$t("credit_balance"),
      ];
      const rows = this.rows.map((row) => [row.userId, row.name, row.ordersCount, row.ordersTotal, row.spent, row.balance]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
